import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import Drawer from "../../components/Drawer";
import { createDbOnlyProduct } from "../../slices/inventorySkuMappingSlice";

const FIXED_DEFAULTS = [
    { label: "Eden Status", value: "DELETED" },
    { label: "Excluded from Forecast", value: "Yes" },
    { label: "Product Status", value: "IN_ACTIVE" },
    { label: "Excluded from Forecast Demand", value: "Yes" },
];

const BASIC_FIELDS = [
    { key: "sku", label: "SKU", required: true, placeholder: "e.g. OLD-SKU-BLK" },
    { key: "name", label: "Name", placeholder: "Product name" },
    { key: "image", label: "Image URL" },
    { key: "barcode", label: "Barcode" },
    { key: "color", label: "Color" },
    { key: "tags", label: "Tags", placeholder: "Comma-separated tags" },
    { key: "vendors_skus", label: "Vendor SKUs", placeholder: "Comma-separated vendor SKUs" },
];

function createInitialFormState() {
    const state = {};
    BASIC_FIELDS.forEach(({ key }) => {
        state[key] = "";
    });
    return state;
}

function SectionLabel({ children }) {
    return (
        <div className="flex items-center gap-2 my-2 first:mt-0">
            <div className="w-0.5 h-4 bg-gray-900 rounded-full shrink-0" />
            <span className="text-xs font-bold uppercase tracking-widest text-gray-500">{children}</span>
        </div>
    );
}

function FieldLabel({ children, required }) {
    return (
        <label className="text-xs text-gray-500 mb-1 block">
            {children}
            {required ? <span className="text-red-500 ml-0.5">*</span> : null}
        </label>
    );
}

function TextInput({ id, value, onChange, placeholder, required }) {
    return (
        <input
            id={id}
            type="text"
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            required={required}
            className="w-full h-8 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900"
        />
    );
}

function buildSubmitPayload(form) {
    const payload = {};
    BASIC_FIELDS.forEach(({ key }) => {
        const value = form[key]?.trim?.() ?? form[key];
        if (value !== "" && value != null) payload[key] = value;
    });
    return payload;
}

const AddProductDrawer = ({ isOpen, onClose, onSuccess }) => {
    const dispatch = useDispatch();
    const [form, setForm] = useState(createInitialFormState);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setForm(createInitialFormState());
        setSubmitting(false);
    }, [isOpen]);

    const canSubmit = useMemo(() => Boolean(form.sku?.trim()) && !submitting, [form.sku, submitting]);

    const updateField = (key) => (e) => {
        setForm((prev) => ({ ...prev, [key]: e.target.value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            const product = await dispatch(createDbOnlyProduct(buildSubmitPayload(form))).unwrap();
            toast.success(`Product "${product?.sku || form.sku.trim()}" created.`);
            onClose();
            onSuccess?.(product);
        } catch (err) {
            const msg = typeof err === "string" ? err : err?.message || "Could not create product.";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    const renderFields = (fields) =>
        fields.map(({ key, label, required, placeholder }) => (
            <div key={key}>
                <FieldLabel required={required}>{label}</FieldLabel>
                <TextInput
                    id={`product-${key}`}
                    value={form[key]}
                    onChange={updateField(key)}
                    placeholder={placeholder}
                    required={required}
                />
            </div>
        ));

    return (
        <Drawer
            isOpen={isOpen}
            onClose={onClose}
            title="Add Product"
            width="720px"
            showActionButtons
            formId="add-db-product-form"
            submitButtonText={submitting ? "Creating…" : "Create Product"}
            submitDisabled={!canSubmit}
            bodyClassName="px-6 py-5"
        >
            <form id="add-db-product-form" onSubmit={handleSubmit}>
                <p className="text-sm text-gray-500 mb-4">
                    Creates a product in the database only (not synced to ShipHero). Fixed defaults apply on save.
                </p>

                <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 mb-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-amber-900 mb-2">
                        Fixed on create
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {FIXED_DEFAULTS.map(({ label, value }) => (
                            <div key={label} className="flex items-center justify-between gap-3 text-sm">
                                <span className="text-amber-900/80">{label}</span>
                                <span className="font-medium text-amber-950">{value}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <SectionLabel>Basic</SectionLabel>
                <div className="flex flex-col gap-4">{renderFields(BASIC_FIELDS)}</div>
            </form>
        </Drawer>
    );
};

export default AddProductDrawer;
